import type { CountryCode } from './countries';
import { countryProfiles } from './countries';
import {
  inventoryItems,
  requisitions,
  connectionTracking,
} from './mock-data';
import type { InventoryItem, Requisition, ConnectionMonthly, KpiCard } from './mock-data';

// Ghana warehouse array for index remapping
const ghWarehouses = countryProfiles.GH.warehouses;

/**
 * Deterministic seeded factor in range [0.85, 1.15].
 * No Math.random(), no Date.now(), no new Date().
 */
export function seededFactor(seed: string): number {
  let sum = 0;
  for (let i = 0; i < seed.length; i++) {
    sum += seed.charCodeAt(i);
  }
  const mod = sum % 31; // 0..30
  // Map 0..30 linearly to 0.85..1.15
  return 0.85 + (mod / 30) * 0.3;
}

export function getInventoryForCountry(code: CountryCode): InventoryItem[] {
  const profile = countryProfiles[code];
  return inventoryItems.map((base) => {
    const whIdx = ghWarehouses.indexOf(base.warehouse as typeof ghWarehouses[number]);
    const warehouse = whIdx >= 0 ? profile.warehouses[whIdx] : profile.warehouses[0];

    const quantity = Math.round(
      base.quantity * profile.dataScale * seededFactor(code + base.itemCode)
    );
    const reorderLevel = Math.max(1, Math.round(base.reorderLevel * profile.dataScale));

    let status: InventoryItem['status'];
    if (quantity === 0) {
      status = 'out_of_stock';
    } else if (quantity <= reorderLevel) {
      status = 'low_stock';
    } else {
      status = 'in_stock';
    }

    return {
      ...base,
      id: `${code}-${base.id}`,
      warehouse,
      quantity,
      reorderLevel,
      status,
    };
  });
}

export function getRequisitionsForCountry(code: CountryCode): Requisition[] {
  const profile = countryProfiles[code];
  return requisitions.map((base, i) => {
    const whIdx = ghWarehouses.indexOf(base.warehouse as typeof ghWarehouses[number]);
    const warehouse = whIdx >= 0 ? profile.warehouses[whIdx] : profile.warehouses[0];

    // Replace MOEN-REQ prefix with country reqPrefix, keep numeric suffix
    const numericSuffix = base.moenId.replace(/^[A-Z]+-REQ-/, '');
    const moenId = `${profile.reqPrefix}-${numericSuffix}`;

    const contractor = profile.contractors[i % 5];
    const region = profile.regions[i % 5];
    const department = profile.regions[i % 5];

    const quantityRequested = Math.max(
      1,
      Math.round(base.quantityRequested * profile.dataScale * seededFactor(code + base.moenId))
    );

    const hardcopyRef = base.hardcopyRef.replace('HC-', `${code}-HC-`);

    return {
      ...base,
      id: `${code}-${base.id}`,
      moenId,
      contractor,
      department,
      region,
      warehouse,
      quantityRequested,
      hardcopyRef,
      itemCode: base.itemCode,
    };
  });
}

export function getConnectionTrackingForCountry(code: CountryCode): ConnectionMonthly[] {
  const profile = countryProfiles[code];

  // First pass: compute derived newConnections
  const derived = connectionTracking.map((base) => ({
    month: base.month,
    newConnections: Math.round(
      base.newConnections * profile.dataScale * seededFactor(code + base.month)
    ),
    cumulative: 0,
  }));

  // The cumulative series must end at profile.baseCumulative
  // Start = baseCumulative - sum(all newConnections)
  const totalNew = derived.reduce((sum, d) => sum + d.newConnections, 0);
  let running = profile.baseCumulative - totalNew;

  for (let i = 0; i < derived.length; i++) {
    running += derived[i].newConnections;
    derived[i].cumulative = running;
  }

  return derived;
}

export function getKpisForCountry(code: CountryCode): KpiCard[] {
  const profile = countryProfiles[code];
  const inventory = getInventoryForCountry(code);
  const reqs = getRequisitionsForCountry(code);
  const connections = getConnectionTrackingForCountry(code);

  const totalMaterials = inventory.reduce((sum, item) => sum + item.quantity, 0);
  const activeRequisitions = reqs.filter((r) => r.status !== 'released').length;
  const pendingCdApprovals = reqs.filter((r) => r.stage === 'chief_director_approval').length;
  const trackedConnections = connections[connections.length - 1]?.cumulative ?? profile.baseCumulative;

  return [
    {
      id: `${code}-kpi-001`,
      label: 'Total Line Materials',
      value: totalMaterials.toLocaleString('en-US'),
      delta: '3.2% vs last quarter',
      deltaPositive: true,
      iconName: 'Package',
      iconColor: '#0D3B2E',
    },
    {
      id: `${code}-kpi-002`,
      label: 'Active Requisitions',
      value: activeRequisitions.toLocaleString('en-US'),
      delta: '8 fewer than last week',
      deltaPositive: true,
      iconName: 'ClipboardList',
      iconColor: '#C9973A',
    },
    {
      id: `${code}-kpi-003`,
      label: 'Pending CD Approvals',
      value: pendingCdApprovals.toLocaleString('en-US'),
      delta: 'Urgent, action required',
      deltaPositive: false,
      iconName: 'AlertTriangle',
      iconColor: '#DC2626',
      alert: true,
      alertLabel: 'URGENT',
    },
    {
      id: `${code}-kpi-004`,
      label: 'Tracked Connections',
      value: trackedConnections.toLocaleString('en-US'),
      delta: `${profile.newThisMonth} this month`,
      deltaPositive: true,
      iconName: 'Zap',
      iconColor: '#4A9EB5',
    },
  ];
}
